import { MODAL_HEIGHT, MODAL_WIDTH } from "@/constants";
import type { LoginMethod } from "@/context/PassportProvider";
import {
  LARGE_VERTICAL_BREAKPOINT,
  useIsLandscapeDisplayMode,
} from "@/hooks/useIsLandscapeDisplayMode";
import {
  Body,
  Box,
  ButtCon,
  FormControl,
  FramedImage,
  Heading,
  Link,
  Modal,
  Stack,
  TextInput,
} from "@biom3/react"; // Adjust imports based on your UI library
import { Fit, Layout, useRive } from "@rive-app/react-canvas";
import type React from "react";
import { useMemo } from "react";
import { useController, useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import MarketingConsent from "./MarketingConsent";

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLoginMethodSelect: (method: LoginMethod) => void;
}

interface FormValues {
  address: string;
}

export const LoginModal: React.FC<LoginModalProps> = ({
  isOpen,
  onClose,
  onLoginMethodSelect,
}) => {
  const { t } = useTranslation();

  const isLandscapeDisplayMode = useIsLandscapeDisplayMode(
    LARGE_VERTICAL_BREAKPOINT,
  );

  const { RiveComponent } = useRive({
    src: "https://biome.immutable.com/hosted-assets/rive/immutable_reveal.riv",
    autoplay: true,
    layout: new Layout({ fit: Fit.Cover }),
  });

  const isFacebookAuth = useMemo(() => {
    if (typeof window !== "undefined") {
      const ua = window.navigator.userAgent || window.navigator.vendor; //@ts-ignore
      return /FBAN|FBAV|FBBV|FBDV|FBMD|FBSN|FBSV|FBSS|FBCR|FBID|FBLC|FBOP|FB_IAB|Instagram/.test(
        ua,
      );
    }
  }, []);

  const {
    handleSubmit,
    formState: { errors },
    control,
  } = useForm<FormValues>({});

  const { field: emailField } = useController({
    name: "address",
    control,
    rules: {
      pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      required: true,
    },
  });

  const onSubmit = (fieldValues: FormValues) => {
    onLoginMethodSelect({
      email: fieldValues.address,
    });
  };

  return (
    <Modal visible={isOpen} onCloseModal={onClose}>
      <Modal.Content
        sx={{
          d: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          justifyContent: "center",
          borderRadius: "base.borderRadius.x10",
          overflow: "hidden",
          background: "base.color.brand.2",
          height: MODAL_HEIGHT,
          width: {
            default: "calc(100dvw - 40px)",
            small: MODAL_WIDTH,
          },
          gap: isLandscapeDisplayMode ? "base.spacing.x10" : "base.spacing.x18",
        }}
      >
        <Heading
          sx={{
            textTransform: "titlecase",
            textAlign: "center",
            color: "base.gradient.1",
            whiteSpace: "pre-line",
            alignSelf: "center",
          }}
          size={isLandscapeDisplayMode ? "small" : "medium"}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              flexDirection: "column",
              alignItems: "center",
              gap: "base.spacing.x4",
            }}
          >
            <FramedImage
              use={<RiveComponent />}
              circularFrame
              sx={{
                width: "base.spacing.x20",
                height: "base.spacing.x20",
                display: isLandscapeDisplayMode ? "none" : "flex",
              }}
            />
            <Box>{t("login_to_your_account")}</Box>
          </Box>
        </Heading>
        <Stack
          id="actions"
          testId="login_actions"
          sx={{
            alignItems: "center",
          }}
          gap="base.spacing.x4"
        >
          <Box
            id="webauth"
            testId="webauth"
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              gap: "base.spacing.x8",
              marginBottom: "base.spacing.x4",
            }}
          >
            {!isFacebookAuth && (
              <ButtCon
                testId="webauth_google"
                size={isLandscapeDisplayMode ? "medium" : "large"}
                onClick={(event) => {
                  onLoginMethodSelect("google");
                }}
              >
                <ButtCon.SvgIcon viewBox="0 0 40 40" sx={{ w: "40px" }}>
                  <path
                    d="M29.6 20.2273C29.6 19.5182 29.5364 18.8364 29.4182 18.1818H20V22.05H25.3818C25.15 23.3 24.4455 24.3591 23.3864 25.0682V27.5773H26.6182C28.5091 25.8364 29.6 23.2727 29.6 20.2273Z"
                    fill="#4285F4"
                  />
                  <path
                    d="M20 30C22.7 30 24.9636 29.1045 26.6181 27.5773L23.3863 25.0682C22.4909 25.6682 21.3454 26.0227 20 26.0227C17.3954 26.0227 15.1909 24.2636 14.4045 21.9H11.0636V24.4909C12.7091 27.7591 16.0909 30 20 30Z"
                    fill="#34A853"
                  />
                  <path
                    d="M14.4045 21.9C14.2045 21.3 14.0909 20.6591 14.0909 20C14.0909 19.3409 14.2045 18.7 14.4045 18.1V15.5091H11.0636C10.3864 16.8591 10 18.3864 10 20C10 21.6136 10.3864 23.1409 11.0636 24.4909L14.4045 21.9Z"
                    fill="#FBBC04"
                  />
                  <path
                    d="M20 13.9773C21.4681 13.9773 22.7863 14.4818 23.8227 15.4727L26.6909 12.6045C24.9591 10.9909 22.6954 10 20 10C16.0909 10 12.7091 12.2409 11.0636 15.5091L14.4045 18.1C15.1909 15.7364 17.3954 13.9773 20 13.9773Z"
                    fill="#E94235"
                  />
                </ButtCon.SvgIcon>
              </ButtCon>
            )}
            <ButtCon
              icon="Apple"
              testId="webauth_apple"
              onClick={(event) => {
                onLoginMethodSelect("apple");
              }}
              size={isLandscapeDisplayMode ? "medium" : "large"}
            />
            <ButtCon
              testId="webauth_facebook"
              onClick={(event) => {
                onLoginMethodSelect("facebook");
              }}
              size={isLandscapeDisplayMode ? "medium" : "large"}
            >
              <ButtCon.SvgIcon viewBox="0 0 300 300">
                <path
                  d="M300 150C300 67.158 232.842 0 150 0C67.158 0 0 67.158 0 150C0 220.344 48.432 279.372 113.766 295.584V195.84H82.836V150H113.766V130.248C113.766 79.194 136.872 55.53 186.996 55.53C196.5 55.53 212.898 57.396 219.606 59.256V100.806C216.066 100.434 209.916 100.248 202.278 100.248C177.684 100.248 168.18 109.566 168.18 133.788V150H217.176L208.758 195.84H168.18V298.902C242.454 289.932 300.006 226.692 300.006 150H300Z"
                  fill="#0866FF"
                />
                <path
                  d="M208.752 195.84L217.17 150H168.174V133.788C168.174 109.566 177.678 100.248 202.272 100.248C209.91 100.248 216.06 100.434 219.6 100.806V59.256C212.892 57.39 196.494 55.53 186.99 55.53C136.866 55.53 113.76 79.194 113.76 130.248V150H82.8301V195.84H113.76V295.584C125.364 298.464 137.502 300 149.994 300C156.144 300 162.21 299.622 168.168 298.902V195.84H208.746H208.752Z"
                  fill="white"
                />
              </ButtCon.SvgIcon>
            </ButtCon>
          </Box>
          <Body
            id="or"
            sx={{
              textAlign: "center",
            }}
          >
            {t("logging_method_or")}
          </Body>
          <Box sx={{ width: "100%", paddingX: "base.spacing.x8" }}>
            <form noValidate onSubmit={handleSubmit(onSubmit)}>
              <FormControl
                validationStatus={
                  errors.address === undefined ? undefined : "error"
                }
              >
                <TextInput
                  placeholder={t("address_placeholder")}
                  autoComplete="email"
                  onChange={emailField.onChange}
                  onBlur={emailField.onBlur}
                  value={emailField.value}
                  type="email"
                  name={emailField.name}
                  inputRef={emailField.ref}
                >
                  <TextInput.Icon icon="EmailAddress" />
                  <TextInput.StatefulButtCon
                    icon="ArrowForward"
                    type="submit"
                  />
                </TextInput>
                {errors?.address?.type === "required" && (
                  <FormControl.Validation testId="error_input_required">
                    {t("error_email_required")}
                  </FormControl.Validation>
                )}
                {errors?.address?.type === "pattern" && (
                  <FormControl.Validation testId="error_input_format">
                    {t("error_email_format")}
                  </FormControl.Validation>
                )}
              </FormControl>
            </form>
          </Box>
        </Stack>
        <Stack
          sx={{
            paddingX: "base.spacing.x8",
          }}
        >
          <MarketingConsent />
          <Body
            sx={{
              textAlign: "center",
              color: "base.color.text.body.secondary",
            }}
            size={isLandscapeDisplayMode ? "xxSmall" : "xSmall"}
          >
            <Trans i18nKey="data_access_statement">
              You&apos;ll be giving access to your Passport profile and email
              address to {"Immutable Play"} as per our
              <Link
                rc={
                  <a
                    href="https://www.immutable.com/terms-of-service"
                    target="_blank"
                    rel="noreferrer"
                  />
                }
              >
                Terms of Service
              </Link>
              ,{" "}
              <Link
                rc={
                  <a
                    href="https://www.immutable.com/privacy-policy"
                    target="_blank"
                    rel="noreferrer"
                  />
                }
              >
                Privacy Policy
              </Link>{" "}
              and
              <Link
                testId="collection_statement"
                size="xSmall"
                rc={
                  <a
                    href="https://www.immutable.com/collection-statement"
                    target="_blank"
                    rel="noreferrer"
                  />
                }
              >
                Collection Statement
              </Link>
            </Trans>
          </Body>
        </Stack>
      </Modal.Content>
    </Modal>
  );
};
